import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ltc_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      clearToken();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        const redirect = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.href = `/login?redirect=${redirect}`;
      }
    }
    return Promise.reject(error);
  },
);

export const PlayersAPI = {
  list: (params = {}) => api.get("/players", { params }).then((r) => r.data),
  get: (slugOrId) => api.get(`/players/${slugOrId}`).then((r) => r.data),
  create: (data) => api.post("/players", data).then((r) => r.data),
  update: (id, data) => api.put(`/players/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/players/${id}`).then((r) => r.data),
  seed: () => api.post("/seed").then((r) => r.data),
};

export const TournamentsAPI = {
  list: (params = {}) => api.get("/tournaments", { params }).then((r) => r.data),
  get: (slugOrId) => api.get(`/tournaments/${slugOrId}`).then((r) => r.data),
  create: (data) => api.post("/tournaments", data).then((r) => r.data),
  update: (id, data) => api.put(`/tournaments/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/tournaments/${id}`).then((r) => r.data),
  assignOrganizer: (id, organizer_id) =>
    api.put(`/tournaments/${id}/organizer`, { organizer_id }).then((r) => r.data),
  applyUgsOfficial: () =>
    api.post("/tournaments/apply-ugs-official").then((r) => r.data),
};

export const AuthAPI = {
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
  googleExchange: (session_id) =>
    api.post("/auth/google/exchange", { session_id }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

export const SettingsAPI = {
  get: () => api.get("/settings").then((r) => r.data),
  update: (data) => api.put("/settings", data).then((r) => r.data),
};

export const ShowcaseAPI = {
  get: () => api.get("/showcase-cards").then((r) => r.data),
  update: (cards) => api.put("/showcase-cards", { cards }).then((r) => r.data),
  getContent: () => api.get("/showcase-content").then((r) => r.data),
  updateContent: (content) =>
    api.put("/showcase-content", { content }).then((r) => r.data),
};

export const OrganizersAPI = {
  list: () => api.get("/organizers").then((r) => r.data),
  create: (data) => api.post("/organizers", data).then((r) => r.data),
  update: (id, data) => api.put(`/organizers/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/organizers/${id}`).then((r) => r.data),
};

export const SearchAPI = {
  query: (q) =>
    api.get("/search", { params: { q } }).then((r) => r.data),
};

export const SeedAPI = {
  full: () => api.post("/seed/full").then((r) => r.data),
  wipe: () => api.delete("/seed/full").then((r) => r.data),
};

export const CoachesAPI = {
  list: () => api.get("/coaches").then((r) => r.data),
  create: (data) => api.post("/coaches", data).then((r) => r.data),
  update: (id, data) => api.put(`/coaches/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/coaches/${id}`).then((r) => r.data),
};

export const MatchAPI = {
  record: (playerId, deltas) =>
    api.post(`/players/${playerId}/match`, { deltas }).then((r) => r.data),
};

export const CoachTournamentsAPI = {
  list: () => api.get("/coach/tournaments").then((r) => r.data),
  create: (data) => api.post("/coach/tournaments", data).then((r) => r.data),
  update: (id, data) =>
    api.put(`/coach/tournaments/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/coach/tournaments/${id}`).then((r) => r.data),
  updateTeam: (id, payload) =>
    api.put(`/coach/tournaments/${id}/team`, payload).then((r) => r.data),
  generateInvite: (id) =>
    api.post(`/coach/tournaments/${id}/invite`).then((r) => r.data),
  revokeInvite: (id) =>
    api.delete(`/coach/tournaments/${id}/invite`).then((r) => r.data),
};

export const CoachPlayersAPI = {
  create: (data) => api.post("/coach/players", data).then((r) => r.data),
  remove: (id) => api.delete(`/coach/players/${id}`).then((r) => r.data),
};

export const CoachTeamsAPI = {
  list: () => api.get("/coach/teams").then((r) => r.data),
  update: (id, data) => api.put(`/coach/teams/${id}`, data).then((r) => r.data),
};

export const TournamentJoinAPI = {
  preview: (code) =>
    api.get(`/tournaments/join/${code}`).then((r) => r.data),
  submit: (code, data) =>
    api.post(`/tournaments/join/${code}`, data).then((r) => r.data),
};

export const TeamsAPI = {
  list: (params = {}) => api.get("/teams", { params }).then((r) => r.data),
  get: (slugOrId) => api.get(`/teams/${slugOrId}`).then((r) => r.data),
  create: (data) => api.post("/teams", data).then((r) => r.data),
  update: (id, data) => api.put(`/teams/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/teams/${id}`).then((r) => r.data),
  migrate: () => api.post("/teams/migrate-from-clubs").then((r) => r.data),
};

export const LeadsAPI = {
  submit: (data) => api.post("/leads", data).then((r) => r.data),
  list: () => api.get("/leads").then((r) => r.data),
  update: (id, data) => api.put(`/leads/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/leads/${id}`).then((r) => r.data),
};

export const ChampionshipsAPI = {
  list: (params = {}) => api.get("/championships", { params }).then((r) => r.data),
  get: (slugOrId) => api.get(`/championships/${slugOrId}`).then((r) => r.data),
  create: (data) => api.post("/championships", data).then((r) => r.data),
  update: (id, data) => api.put(`/championships/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/championships/${id}`).then((r) => r.data),
  assignOrganizer: (id, organizer_id) =>
    api.put(`/championships/${id}/organizer`, { organizer_id }).then((r) => r.data),
};

export const StatsAPI = {
  views: () => api.get("/stats/views").then((r) => r.data),
};

export const AssociationsAPI = {
  list: () => api.get("/associations").then((r) => r.data),
  get: (slugOrId) => api.get(`/associations/${slugOrId}`).then((r) => r.data),
  create: (data) => api.post("/associations", data).then((r) => r.data),
  update: (id, data) => api.put(`/associations/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/associations/${id}`).then((r) => r.data),
};

export const CompaniesAPI = {
  list: () => api.get("/companies").then((r) => r.data),
  get: (slugOrId) => api.get(`/companies/${slugOrId}`).then((r) => r.data),
  create: (data) => api.post("/companies", data).then((r) => r.data),
  update: (id, data) => api.put(`/companies/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/companies/${id}`).then((r) => r.data),
};

const VIDEO_CHUNK_SIZE = 4 * 1024 * 1024; // 4MB — safely under proxy limits

export const UploadsAPI = {
  image: async (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/uploads/image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return r.data;
  },
  video: async (file, onProgress) => {
    const uploadId = crypto.randomUUID().replace(/-/g, "");
    const total = Math.max(1, Math.ceil(file.size / VIDEO_CHUNK_SIZE));
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append(
        "file",
        file.slice(i * VIDEO_CHUNK_SIZE, (i + 1) * VIDEO_CHUNK_SIZE),
        file.name,
      );
      await api.post("/uploads/video/chunk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onProgress?.(Math.round(((i + 1) / total) * 90));
    }
    const r = await api.post("/uploads/video/complete", {
      upload_id: uploadId,
      filename: file.name,
      total_chunks: total,
    });
    onProgress?.(100);
    return r.data;
  },
};

/**
 * Resolves an image URL for display. If it's a relative `/api/uploads/...`
 * path returned by our upload endpoint, prefix with the backend host so
 * <img src=...> resolves correctly. External URLs are returned as-is.
 */
export const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/uploads/") || url.startsWith("/uploads/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

export function formatApiError(err, fallback = "Une erreur est survenue") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  return String(detail);
}
